/**
 * The Vihangam Yog mark — the gold अ rendered as a vector glyph in
 * Tiro Devanagari Sanskrit. Crisp at every size, no PNG artefacts,
 * recolours via currentColor in dark/light contexts. The amber colour
 * is locked regardless of surrounding palette so the mark always
 * reads as "the saffron अ."
 */
export function Mark({
  size = 32,
  alt = "Vihangam Yog",
  colour,
}: {
  size?: number;
  alt?: string;
  colour?: string;
}) {
  return (
    <span
      role="img"
      aria-label={alt}
      className="font-devanagari"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        lineHeight: `${size}px`,
        fontSize: size * 1.08,
        color: colour ?? "var(--color-gold-light)",
        textAlign: "center",
        fontWeight: 400,
        userSelect: "none",
      }}
    >
      अ
    </span>
  );
}

/**
 * Vertical / stacked lockup — mark on top, wordmark + tagline below,
 * with a small hand-drawn calligraphic flourish under the tagline.
 * Use this where the surface has room and the brand wants presence —
 * auth pages, footers, brochure-style headers. Never on the website
 * nav (too tall).
 */
export function LockupStacked({
  glyphSize = 56,
  wordmarkSize = 13,
  taglineSize = 13,
  showFlourish = true,
  tone = "dark",
}: {
  glyphSize?: number;
  wordmarkSize?: number;
  taglineSize?: number;
  showFlourish?: boolean;
  tone?: "dark" | "light";
}) {
  const wordColour =
    tone === "light" ? "var(--color-warm-white)" : "var(--color-warm-brown)";
  const tagColour =
    tone === "light"
      ? "rgba(247,230,191,0.78)"
      : "var(--color-warm-brown-light)";
  const flourishColour =
    tone === "light" ? "var(--color-gold-light)" : "var(--color-gold)";

  return (
    <div className="flex flex-col items-center gap-3">
      <Mark size={glyphSize} />
      <div className="flex flex-col items-center gap-1 leading-tight">
        <span
          className="font-medium tracking-[0.32em] uppercase"
          style={{
            fontSize: wordmarkSize,
            color: wordColour,
          }}
        >
          Vihangam Yog
        </span>
        <span
          className="font-serif italic"
          style={{
            fontSize: taglineSize,
            color: tagColour,
            letterSpacing: "0.02em",
          }}
        >
          Science of Consciousness
        </span>
      </div>
      {showFlourish && (
        <svg
          width={glyphSize * 0.62}
          height={glyphSize * 0.36}
          viewBox="0 0 48 28"
          fill="none"
          aria-hidden
          className="mt-1.5 opacity-75"
        >
          <g
            stroke={flourishColour}
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M24 4.5 C20.8 10 20.8 14 24 18.5 C27.2 14 27.2 10 24 4.5 Z" />
            <path d="M22.5 17.5 C17 12.5 12.5 12 9.5 13 C11.5 17.5 16.5 19.8 23 20" />
            <path d="M25.5 17.5 C31 12.5 35.5 12 38.5 13 C36.5 17.5 31.5 19.8 25 20" />
            <path d="M9 22.5 C18 26 30 26 39 22.5" />
          </g>
        </svg>
      )}
    </div>
  );
}
